/**
 * DEMLoader - DEM (GeoTIFF) 파일 로더
 * 고도 데이터를 색상으로 시각화하여 표시
 */

import * as GeoTIFF from 'geotiff';
import ImageLayer from 'ol/layer/Image';
import ImageCanvasSource from 'ol/source/ImageCanvas';
import { transformExtent } from 'ol/proj';
import { layerManager } from '../core/LayerManager.js';
import { mapManager } from '../core/MapManager.js';
import { detectCrs } from '../core/CrsDetector.js';
import { escapeHtml } from '../utils/escapeHtml.js';
import { eventBus, Events } from '../utils/EventBus.js';
import { makeDraggable } from '../utils/DraggableElement.js';

// 기본 색상 램프 (저고도 -> 고고도)
const DEFAULT_COLOR_RAMP = [
  { value: 0, color: [0, 97, 71] },      // 진한 녹색 (저지대)
  { value: 0.15, color: [34, 139, 34] }, // 녹색
  { value: 0.3, color: [154, 205, 50] }, // 연두색
  { value: 0.45, color: [255, 255, 0] }, // 노란색
  { value: 0.6, color: [255, 165, 0] },  // 주황색
  { value: 0.75, color: [255, 69, 0] },  // 빨간주황
  { value: 0.9, color: [139, 69, 19] },  // 갈색
  { value: 1.0, color: [255, 255, 255] } // 흰색 (고산)
];

class DEMLoader {
  constructor() {
    this.colorRamp = DEFAULT_COLOR_RAMP;
    this.legends = new Map(); // layerId -> legend element

    // 레이어 삭제 시 범례 정리
    eventBus.on(Events.LAYER_REMOVED, (data) => {
      if (data && data.layerId) this.removeLegend(data.layerId);
    });

    // 레이어 이름 변경 → DEM 범례 제목 동기화
    eventBus.on(Events.LAYER_RENAMED, (data) => {
      if (!data || !data.layerId) return;
      const legendEl = this.legends.get(data.layerId);
      if (!legendEl) return;
      const titleEl = legendEl.querySelector('.raster-legend-title');
      if (titleEl && titleEl.textContent !== data.name) titleEl.textContent = data.name;
    });
  }

  /**
   * File 객체로부터 DEM 로드
   * @param {File} file - GeoTIFF 또는 IMG 파일
   * @returns {Promise<string>} 생성된 레이어 ID
   */
  async loadFromFile(file) {
    try {
      console.log('DEM 파일 로드 시작:', file.name);
      const arrayBuffer = await file.arrayBuffer();
      console.log('ArrayBuffer 크기:', arrayBuffer.byteLength);

      const tiff = await GeoTIFF.fromArrayBuffer(arrayBuffer);
      console.log('GeoTIFF 파싱 완료');

      const image = await tiff.getImage();
      console.log('이미지 로드 완료:', image.getWidth(), 'x', image.getHeight());

      // 파일명에서 확장자 제거
      const layerName = file.name.replace(/\.(tif|tiff|geotiff|img)$/i, '');

      return await this.createDEMLayer(image, layerName);
    } catch (error) {
      console.error('DEM 로드 에러:', error);
      // IMG 파일이 GeoTIFF 형식이 아닌 경우 에러 처리
      if (file.name.toLowerCase().endsWith('.img')) {
        throw new Error('IMG 파일을 읽을 수 없습니다. GeoTIFF 형식의 IMG 파일만 지원됩니다.');
      }
      throw new Error('래스터 파일 로드 실패: ' + error.message);
    }
  }

  /**
   * URL로부터 DEM 로드
   * @param {string} url - GeoTIFF URL
   * @param {string} name - 레이어 이름
   * @param {Object} options - { fitExtent: true } 일괄 로드 시 false로 뷰 점프 방지
   * @returns {Promise<string>} 생성된 레이어 ID
   */
  async loadFromUrl(url, name = 'DEM', options = {}) {
    const tiff = await GeoTIFF.fromUrl(url);
    const image = await tiff.getImage();
    return await this.createDEMLayer(image, name, options);
  }

  /**
   * GeoTIFF 이미지로부터 DEM 레이어 생성
   * @param {Object} image - GeoTIFF 이미지
   * @param {string} name - 레이어 이름
   * @returns {string} 레이어 ID
   */
  async createDEMLayer(image, name, options = {}) {
    const { fitExtent: doFit = true } = options;
    // 래스터 데이터 읽기
    const rasters = await image.readRasters();
    const width = image.getWidth();
    const height = image.getHeight();
    const data = rasters[0]; // 첫 번째 밴드 (고도 값)

    // 범위 및 투영 정보
    const bbox = image.getBoundingBox();
    const geoKeys = image.getGeoKeys();

    // 좌표계 확인
    let extent = bbox;

    // 좌표계 판정은 CrsDetector가 한다 — 다른 로더와 같은 규칙을 쓴다.
    // GeoTIFF는 EPSG 코드를 비워 두고 파라미터만 채워 오는 일이 흔하다.
    const detection = detectCrs({
      epsgCode: geoKeys && (geoKeys.ProjectedCSTypeGeoKey || geoKeys.GeographicTypeGeoKey),
      projParams: geoKeysToParams(geoKeys),
      sampleCoords: [[bbox[0], bbox[1]], [bbox[2], bbox[3]]]
    });
    const sourceProj = detection.confidence === 'unknown' ? null : detection.crs;

    console.log('DEM 좌표계:', sourceProj || '정의되지 않음', detection.reason, 'bbox:', bbox);

    // 판정이 애매하면 첫 후보를 조용히 쓴다 — DEM은 픽셀을 재투영하지 않고 bbox만 변환하는
    // 구조라 확인 창에 그릴 미리보기가 없어(다른 로더의 resolveSourceCrs를 거치지 않음),
    // 사용자에게 선택을 묻지 못한다. 그래도 아무 흔적이 없으면 안 되니 콘솔에는 남긴다.
    // 상태 표시줄 등 UI로 알리는 건 여기서 하지 않는다 — 로더는 UI를 모르는 경계를 지킨다.
    if (detection.confidence === 'ambiguous') {
      const otherCandidates = (detection.candidates || [])
        .slice(1)
        .map((c) => c.crs)
        .join(', ') || '없음';
      console.warn(`DEM 좌표계 판정 애매함 — "${detection.crs}"를 선택함 (다른 후보: ${otherCandidates})`);
    }

    // bbox 값을 분석하여 좌표계 추측
    const guessProjection = (bbox) => {
      const [minX, minY, maxX, maxY] = bbox;

      // EPSG:4326 범위: 경도 -180~180, 위도 -90~90
      if (minX >= -180 && maxX <= 180 && minY >= -90 && maxY <= 90) {
        return 'EPSG:4326';
      }

      // EPSG:3857 범위: 약 -20037508 ~ 20037508
      if (Math.abs(minX) > 180 && Math.abs(maxX) < 20037509 &&
          Math.abs(minY) > 90 && Math.abs(maxY) < 20037509) {
        return 'EPSG:3857';
      }

      // UTM 좌표계 범위 추측 (일반적으로 100000 ~ 900000 사이의 X값)
      if (minX > 100000 && maxX < 1000000 && minY > 0) {
        return 'UTM';
      }

      return null;
    };

    // 수동 좌표 변환 함수
    const toMercator = (lon, lat) => {
      const x = lon * 20037508.34 / 180;
      let y = Math.log(Math.tan((90 + lat) * Math.PI / 360)) / (Math.PI / 180);
      y = y * 20037508.34 / 180;
      return [x, y];
    };

    // 좌표계가 없거나 정의되지 않은 경우 bbox로 추측
    if (!sourceProj) {
      const guessed = guessProjection(bbox);
      console.log('좌표계 추측 결과:', guessed);

      if (guessed === 'EPSG:3857') {
        extent = bbox;
      } else if (guessed === 'EPSG:4326') {
        const min = toMercator(bbox[0], bbox[1]);
        const max = toMercator(bbox[2], bbox[3]);
        extent = [min[0], min[1], max[0], max[1]];
      } else {
        // UTM이나 기타 좌표계 - bbox를 그대로 사용하되 경고
        console.warn('알 수 없는 좌표계, bbox 그대로 사용');
        extent = bbox;
      }
    } else {
      // EPSG:3857로 변환 시도
      try {
        if (sourceProj === 'EPSG:3857') {
          extent = bbox;
        } else {
          extent = transformExtent(bbox, sourceProj, 'EPSG:3857');
        }
      } catch (e) {
        console.warn('좌표계 변환 실패:', e);
        // bbox 값으로 좌표계 추측 후 변환
        const guessed = guessProjection(bbox);
        if (guessed === 'EPSG:4326') {
          const min = toMercator(bbox[0], bbox[1]);
          const max = toMercator(bbox[2], bbox[3]);
          extent = [min[0], min[1], max[0], max[1]];
        } else {
          extent = bbox;
        }
      }
    }

    console.log('최종 extent:', extent);

    // 최소/최대 고도 값 계산 (nodata 값 제외)
    let minVal = Infinity;
    let maxVal = -Infinity;
    const noDataValue = image.getGDALNoData() || -9999;

    for (let i = 0; i < data.length; i++) {
      const val = data[i];
      if (val !== noDataValue && !isNaN(val) && isFinite(val)) {
        if (val < minVal) minVal = val;
        if (val > maxVal) maxVal = val;
      }
    }

    // DEM 데이터 저장
    const demData = {
      data,
      width,
      height,
      extent,
      minVal,
      maxVal,
      noDataValue
    };

    // 레이어 빌드 (프로젝트 복원에서도 재사용)
    return this.buildDEMLayer(demData, name, { doFit });
  }

  /**
   * demData 객체로부터 DEM 래스터 레이어를 생성한다.
   * GeoTIFF 파싱 결과뿐 아니라 프로젝트(.egis) 복원 시에도 사용된다.
   * @param {Object} demData - { data, width, height, extent, minVal, maxVal, noDataValue }
   * @param {string} name - 레이어 이름
   * @param {Object} [options]
   * @param {boolean} [options.doFit=false] - 생성 후 해당 범위로 이동 여부
   * @returns {string} 레이어 ID
   */
  buildDEMLayer(demData, name, options = {}) {
    const { doFit = false } = options;
    const { extent, width, height, minVal, maxVal } = demData;

    // 이미지 캔버스 소스 생성
    const canvasSource = new ImageCanvasSource({
      canvasFunction: (canvasExtent, resolution, pixelRatio, size) => {
        return this.renderDEM(demData, canvasExtent, resolution, size);
      },
      ratio: 1
    });

    // 이미지 레이어 생성
    const olLayer = new ImageLayer({
      source: canvasSource,
      extent: extent,
      opacity: 0.8
    });

    // 레이어 매니저에 추가
    const layerId = layerManager.addLayer({
      name: name,
      type: 'raster',
      olLayer: olLayer,
      source: null,
      geometryType: 'Raster'
    });

    // 레이어에 DEM 메타데이터 저장
    const layerInfo = layerManager.getLayer(layerId);
    if (layerInfo) {
      layerInfo.demData = demData;
      layerInfo.featureCount = `${width}×${height}`;
      layerInfo.opacity = olLayer.getOpacity();
    }

    // 해당 범위로 이동 (일괄 로드에서는 호출 측이 마지막에 통합 fit)
    if (doFit) mapManager.fitExtent(extent);

    // 색상 범례 추가
    this.createLegend(layerId, name, minVal, maxVal);

    return layerId;
  }

  /**
   * DEM 색상 범례 생성 (저고도 녹색 → 고고도 흰색)
   */
  createLegend(layerId, layerName, minVal, maxVal) {
    this.removeLegend(layerId);

    const legendEl = document.createElement('div');
    legendEl.className = 'raster-analysis-legend dem-legend';
    legendEl.id = `dem-legend-${layerId}`;

    const fmt = (v) => Number.isFinite(v) ? `${Math.round(v)} m` : '—';

    legendEl.innerHTML = `
      <div class="raster-legend-title">${escapeHtml(layerName)}</div>
      <div class="raster-legend-content">
        <div class="dem-legend-gradient"></div>
        <div class="raster-legend-labels">
          <span>${fmt(minVal)}</span>
          <span>${fmt((minVal + maxVal) / 2)}</span>
          <span>${fmt(maxVal)}</span>
        </div>
      </div>
    `;

    const mapContainer = document.getElementById('map');
    if (mapContainer) {
      mapContainer.appendChild(legendEl);
      this.legends.set(layerId, legendEl);
      makeDraggable(legendEl, () => mapContainer);
    }
  }

  removeLegend(layerId) {
    const el = this.legends.get(layerId);
    if (el) {
      el.remove();
      this.legends.delete(layerId);
    }
  }

  /**
   * DEM 데이터를 캔버스에 렌더링
   */
  renderDEM(demData, viewExtent, resolution, size) {
    // size는 OL이 부동소수점으로 전달할 수 있음 → 정수로 고정해야
    // Uint8ClampedArray의 비정수 인덱스 쓰기 무시 문제를 피함
    const w = Math.round(size[0]);
    const h = Math.round(size[1]);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');

    const { data, width, height, extent, minVal, maxVal, noDataValue } = demData;

    const demWidth = extent[2] - extent[0];
    const demHeight = extent[3] - extent[1];
    const viewWidth = viewExtent[2] - viewExtent[0];
    const viewHeight = viewExtent[3] - viewExtent[1];

    const imageData = ctx.createImageData(w, h);
    const pixels = imageData.data;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const mapX = viewExtent[0] + (x / w) * viewWidth;
        const mapY = viewExtent[3] - (y / h) * viewHeight;

        const demX = Math.floor(((mapX - extent[0]) / demWidth) * width);
        const demY = Math.floor(((extent[3] - mapY) / demHeight) * height);

        const pixelIndex = (y * w + x) * 4;

        if (demX >= 0 && demX < width && demY >= 0 && demY < height) {
          const dataIndex = demY * width + demX;
          const value = data[dataIndex];

          if (value !== noDataValue && !isNaN(value) && isFinite(value)) {
            const normalized = (value - minVal) / (maxVal - minVal);
            const color = this.getColorForValue(normalized);

            pixels[pixelIndex] = color[0];
            pixels[pixelIndex + 1] = color[1];
            pixels[pixelIndex + 2] = color[2];
            pixels[pixelIndex + 3] = 255;
          } else {
            pixels[pixelIndex + 3] = 0;
          }
        } else {
          pixels[pixelIndex + 3] = 0;
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
  }

  /**
   * 정규화된 값에 대한 색상 반환
   * @param {number} normalized - 0~1 사이의 정규화된 값
   * @returns {number[]} [R, G, B]
   */
  getColorForValue(normalized) {
    const ramp = this.colorRamp;

    // 범위 내 색상 찾기
    for (let i = 0; i < ramp.length - 1; i++) {
      if (normalized >= ramp[i].value && normalized <= ramp[i + 1].value) {
        const ratio = (normalized - ramp[i].value) / (ramp[i + 1].value - ramp[i].value);
        const c1 = ramp[i].color;
        const c2 = ramp[i + 1].color;

        return [
          Math.round(c1[0] + (c2[0] - c1[0]) * ratio),
          Math.round(c1[1] + (c2[1] - c1[1]) * ratio),
          Math.round(c1[2] + (c2[2] - c1[2]) * ratio)
        ];
      }
    }

    // 범위 외
    if (normalized <= 0) return ramp[0].color;
    return ramp[ramp.length - 1].color;
  }

  /**
   * 색상 램프 설정
   * @param {Array} ramp - 색상 램프 배열
   */
  setColorRamp(ramp) {
    this.colorRamp = ramp;
  }

  /**
   * 기본 색상 램프로 리셋
   */
  resetColorRamp() {
    this.colorRamp = DEFAULT_COLOR_RAMP;
  }

  /**
   * 미리 정의된 색상 램프들
   */
  static COLOR_RAMPS = {
    terrain: DEFAULT_COLOR_RAMP,
    grayscale: [
      { value: 0, color: [0, 0, 0] },
      { value: 1, color: [255, 255, 255] }
    ],
    viridis: [
      { value: 0, color: [68, 1, 84] },
      { value: 0.25, color: [59, 82, 139] },
      { value: 0.5, color: [33, 145, 140] },
      { value: 0.75, color: [94, 201, 98] },
      { value: 1, color: [253, 231, 37] }
    ],
    cool: [
      { value: 0, color: [0, 0, 139] },
      { value: 0.5, color: [0, 191, 255] },
      { value: 1, color: [255, 255, 255] }
    ],
    hot: [
      { value: 0, color: [0, 0, 0] },
      { value: 0.33, color: [230, 0, 0] },
      { value: 0.66, color: [255, 200, 0] },
      { value: 1, color: [255, 255, 255] }
    ]
  };
}

/**
 * GeoTIFF GeoKeys를 CrsDetector가 읽는 파라미터 모양으로 바꾼다.
 * 정부 GeoTIFF는 EPSG 코드를 비워 두고 파라미터만 채워 오는 일이 흔하다.
 *
 * @returns {{lon0:number, lat0:number, x0:number, y0:number, k:number, ellps:string}|null}
 */
export function geoKeysToParams(geoKeys) {
  // ProjCoordTransGeoKey 1 = 횡축 메르카토르. 한국 좌표계는 모두 여기 해당한다.
  if (!geoKeys || geoKeys.ProjCoordTransGeoKey !== 1) return null;

  const a = geoKeys.GeogSemiMajorAxisGeoKey;
  let ellps = null;
  if (a === undefined) ellps = null;
  else if (Math.abs(a - 6378137) < 0.1) ellps = 'grs80';
  else if (Math.abs(a - 6377397.155) < 0.1) ellps = 'bessel';

  const lon0 = geoKeys.ProjNatOriginLongGeoKey;
  const x0 = geoKeys.ProjFalseEastingGeoKey;
  const y0 = geoKeys.ProjFalseNorthingGeoKey;
  if (lon0 === undefined || x0 === undefined || y0 === undefined) return null;

  return {
    lon0,
    lat0: geoKeys.ProjNatOriginLatGeoKey === undefined ? 0 : geoKeys.ProjNatOriginLatGeoKey,
    x0,
    y0,
    k: geoKeys.ProjScaleAtNatOriginGeoKey === undefined ? 1 : geoKeys.ProjScaleAtNatOriginGeoKey,
    ellps
  };
}

// 싱글톤 인스턴스
export const demLoader = new DEMLoader();
